/**
 * @category LeftSide
 * @component
 * @module Files/fileProcessing/FileProcessingWindow
 * @description This component is a window that pops up when user tries to upload some files and has *no preset*
 * selected in loading options. This component then lets user select options for the file and then process it.
 * User can also save the options as a preset for later use or as an automatic loading option.
 */
import React, { useState, useEffect, useContext, useRef } from "react";
import RawAndSolved from "./RawAndSolved";
import ProcessingOptions from "./ProcessingOptions";
import { DashboardContext } from "../../../../context/DashboardContext";
import { GeneralContext } from "../../../../context/GeneralContext";
import FileList from "./FileList";
import "./fileProcessing.scss";
// import { Button } from "@mui/material";
// import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { generateWarningObject, hasProperty } from "../../../../utils/helpers";
import {
  deepCopyFiles,
  getNextAvailableIndex,
  readFile,
} from "./processingLogic";
import { processFileWithOptions } from "./processingLogic";
import { deepCopy } from "../../Models/modelLogic";
import CloseButton from "../../../commonComponents/CloseButton";
import CustomButton from "../../../commonComponents/CustomButton";
import { file as tutorialDataRaman } from "../../Tutorial/tutorialData/tutorialDataRaman";
import { createTutorialFile } from "../../Tutorial/tutorialData/tutorialDataEllipsometry";
import { createTutorial3Files } from "../../Tutorial/tutorialData/tutorialDataDataLoading";
import { createTutorial4Files } from "../../Tutorial/tutorialData/tutorialDataSimpleCoating";
import {
  useTutorialProgress,
  useTutorial,
} from "../../../../context/TutorialContext";

function FileProcessingWindow(props) {
  const { cancelProcessing, solveProcessing, modalId } = props;
  const {
    filePresetsLoadingOptions,
    setfilePresetsLoadingOptions,
    fileID,
    setFileID,
    setWarnings,
    setNewWarningCount,
    tutorialOpen,
    defaultXAxisUnit,
  } = useContext(DashboardContext);
  const { limitedToast, recordedErrorLog } = useContext(GeneralContext);
  const { monitorStateChange } = useTutorialProgress();
  const { currentTutorial, runningTutorial } = useTutorial();
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [currentFileContent, setCurrentFileContent] = useState(null);
  const [processedContent, setProcessedContent] = useState("");
  const [idForFile, setIdForFile] = useState(fileID);
  const [solvedList, setSolvedList] = useState([]);
  const [done, setDone] = useState(false);
  const [filesToProcess, setFilesToProcess] = useState([]);
  const [currentOptions, setCurrentOptions] = useState({});

  useEffect(() => {
    if (
      done &&
      processedContent === "" &&
      solvedList.every((file) => file !== null) &&
      filesToProcess.length > 0
    ) {
      handleFinishLoad();
    }
  }, [done, processedContent, solvedList]);

  useEffect(() => {
    setIdForFile(fileID);
  }, [fileID]);

  useEffect(() => {
    try {
      if (currentFileIndex <= filesToProcess.length - 1) {
        readFile(
          filesToProcess[currentFileIndex],
          generateWarningObject,
          setWarnings,
          setNewWarningCount,
          limitedToast,
          recordedErrorLog
        )
          .then((readContent) => {
            setCurrentFileContent(readContent);
          })
          .catch(() => {
            setCurrentFileContent(null);
            setCurrentFileIndex((old) => old + 1);
          });
      } else {
        setDone(true);
      }
    } catch (error) {
      recordedErrorLog("currentFileIndex useEffect failure: ", error);
    }
  }, [currentFileIndex]);

  const handleSave = (optionObject) => {
    try {
      const saveName = prompt("Name your option set:");
      if (saveName !== null) {
        const largestIdObj = filePresetsLoadingOptions.reduce((acc, curr) => {
          return acc.id > curr.id ? acc : curr;
        }, filePresetsLoadingOptions[0]);

        setfilePresetsLoadingOptions((old) => [
          ...old,
          {
            presetName: saveName,
            id: largestIdObj.id + 1,
            presetOptions: optionObject,
          },
        ]);
      }
    } catch (error) {
      recordedErrorLog("Save handler failure: ", error);
    }
  };

  const handleApply = (optionObject) => {
    try {
      // Process main column
      const mainDataPoints = processFileWithOptions(
        currentFileContent,
        optionObject,
        recordedErrorLog
      );

      if (mainDataPoints !== null) {
        const file = filesToProcess[currentFileIndex];
        let filesToProcessCopy = deepCopyFiles(filesToProcess, recordedErrorLog);
        filesToProcessCopy[currentFileIndex].solved = true;

        // Process main column
        const mainFileData = createFileData(
          mainDataPoints,
          optionObject,
          file,
          idForFile
        );
        let currentId = idForFile + 1;
        setIdForFile(currentId);
        setFileID((old) => old + 1);

        setFilesToProcess(filesToProcessCopy);
        const updatedSolved = deepCopy(solvedList);
        updatedSolved[currentFileIndex] = mainFileData;
        setSolvedList(updatedSolved);

        // Process additional columns if present
        if (
          optionObject.additionalColumns &&
          optionObject.additionalColumns.length > 0
        ) {
          optionObject.additionalColumns.forEach((column) => {
            if (column.yColNum) {
              // Find shift settings for this additional column
              const columnShiftSettings = optionObject.additionalColumnShifts?.find(
                shift => shift.columnId === column.id
              ) || { yShift: "none", yVal: "" };

              // Create options for this additional column
              const additionalOptions = {
                ...optionObject,
                yColNum: column.yColNum,
                presetName: column.presetName || "",
                yShift: columnShiftSettings.yShift,
                yShiftVal: columnShiftSettings.yVal,
              };

              // Process this additional column
              const additionalDataPoints = processFileWithOptions(
                currentFileContent,
                additionalOptions,
                recordedErrorLog
              );

              if (additionalDataPoints !== null) {
                const additionalFileData = createFileData(
                  additionalDataPoints,
                  additionalOptions,
                  file,
                  currentId
                );

                // Add the additional file to the solved list
                updatedSolved.push(additionalFileData);
                currentId++;
                setIdForFile(currentId);
                setFileID((old) => old + 1);
              }
            }
          });

          // Update the solved list with all additional files
          setSolvedList(updatedSolved);
        }
        if (file.name.toLowerCase().includes("tutorial")) {
          monitorStateChange("tutorial_file_added_to_queue", {
            fileName: file.name,
            tutorial: currentTutorial,
          });
        }
        const nextFreeIndex = getNextAvailableIndex(filesToProcessCopy);
        if (nextFreeIndex !== null) {
          setCurrentFileIndex(nextFreeIndex);
        } else {
          setDone(true);
        }
        setProcessedContent("");
      } else {
        setProcessedContent(mainDataPoints);
      }
    } catch (error) {
      recordedErrorLog("Apply handler failure: ", error);
    }
  };

  // Helper function to create file data
  const createFileData = (dataPoints, optionObject, file, fileId) => {
    // Calculate exact min and max values without rounding
    const maxX = dataPoints.reduce(function (max, p) {
      return p.x > max ? p.x : max;
    }, dataPoints[0].x);

    const minX = dataPoints.reduce(function (min, p) {
      return p.x < min ? p.x : min;
    }, dataPoints[0].x);

    // Create filename with preset prefix if preset name is provided
    const fileName =
      optionObject.presetName && optionObject.presetName.trim() !== ""
        ? `${optionObject.presetName}_${file.name}`
        : file.name;

    // Clean options: remove presetName and additionalColumns
    const cleanOptions = {
      ...optionObject,
      presetName: "",
      additionalColumns: []
    };

    return {
      name: fileName,
      size: file.size,
      type: file.type,
      dataPoints: dataPoints,
      npoints: dataPoints.length,
      edges: [{ min: "", max: "", unit: defaultXAxisUnit }],
      dataRangeMin: minX,
      dataRangeMax: maxX,
      xUnit: optionObject.xUnit,
      ID: fileId,
      options: cleanOptions,
    };
  };

  const handleApplyToAll = async (optionObject) => {
    try {
      const filesToProcessCopy = deepCopyFiles(filesToProcess, recordedErrorLog);
      let newId = idForFile;
      let newFileID = fileID;
      let updatedSolved = deepCopy(solvedList);

      // Create an array of promises for unsolved files
      const processingPromises = filesToProcess.map(async (file, index) => {
        if (file.solved) {
          // If the file is already solved, we resolve immediately.
          return Promise.resolve();
        }
        try {
          const readContent = await readFile(
            file,
            generateWarningObject,
            setWarnings,
            setNewWarningCount,
            limitedToast,
            recordedErrorLog
          );
          // Process main column
          const mainDataPoints = processFileWithOptions(
            readContent,
            optionObject,
            recordedErrorLog
          );

          if (mainDataPoints !== null) {
            filesToProcessCopy[index].solved = true;

            // Process main column
            const mainFileData = createFileData(
              mainDataPoints,
              optionObject,
              file,
              newId
            );
            newId++;
            newFileID++;
            updatedSolved[index] = mainFileData;

            // Process additional columns if present
            if (
              optionObject.additionalColumns &&
              optionObject.additionalColumns.length > 0
            ) {
              optionObject.additionalColumns.forEach((column) => {
                if (column.yColNum) {
                  // Find shift settings for this additional column
                  const columnShiftSettings = optionObject.additionalColumnShifts?.find(
                    shift => shift.columnId === column.id
                  ) || { yShift: "none", yVal: "" };

                  // Create options for this additional column
                  const additionalOptions = {
                    ...optionObject,
                    yColNum: column.yColNum,
                    presetName: column.presetName || "",
                    yShift: columnShiftSettings.yShift,
                    yShiftVal: columnShiftSettings.yVal,
                  };

                  // Process this additional column
                  const additionalDataPoints = processFileWithOptions(
                    readContent,
                    additionalOptions,
                    recordedErrorLog
                  );

                  if (additionalDataPoints !== null) {
                    const additionalFileData = createFileData(
                      additionalDataPoints,
                      additionalOptions,
                      file,
                      newId
                    );

                    // Add the additional file to the solved list
                    updatedSolved.push(additionalFileData);
                    newId++;
                    newFileID++;
                  }
                }
              });
            }
          }
        } catch (error) {
          recordedErrorLog(`Error processing file at index ${index}: `, error);
        }
      });

      // Wait for all file processes to complete.
      await Promise.all(processingPromises);

      // Update the state with the new counters and processed files.
      setIdForFile(newId);
      setFileID(newFileID);
      setFilesToProcess(filesToProcessCopy);
      setSolvedList(updatedSolved);

      // Set the next file to process or mark processing as done.
      const nextFreeIndex = getNextAvailableIndex(filesToProcessCopy);
      if (nextFreeIndex !== null) {
        setCurrentFileIndex(nextFreeIndex);
      } else {
        setDone(true);
      }
      setProcessedContent("");
    } catch (error) {
      recordedErrorLog("Error applying options to all files: ", error);
    }
  };

  const handleCheckOneColumn = (optionObject) => {
    try {
      let dataPoints;
      if (
        hasProperty(filesToProcess[currentFileIndex], "solved") &&
        filesToProcess[currentFileIndex].solved
      ) {
        dataPoints = processFileWithOptions(
          solvedList[currentFileIndex].content,
          optionObject,
          recordedErrorLog
        );
      } else {
        dataPoints = processFileWithOptions(
          currentFileContent,
          optionObject,
          recordedErrorLog
        );
      }
      if (
        dataPoints &&
        dataPoints.length > 0 &&
        optionObject.presetName &&
        optionObject.presetName.trim() !== ""
      ) {
        dataPoints = dataPoints.map((point) => {
          const { y, ...rest } = point;
          return {
            ...rest,
            [optionObject.presetName]: y,
          };
        });
      }
      return dataPoints;
    } catch (error) {
      recordedErrorLog("Check one column failure: ", error);
      return null;
    }
  };

  const handleCheck = (optionObject, skipClear) => {
    try {
      // If optionObject is null, clear the processed content
      if (optionObject === null) {
        setProcessedContent(null);
        return;
      }
      
      // Get main column data points
      let dataPoints = handleCheckOneColumn(optionObject);
      
      // Process additional columns if present
      if (
        optionObject.additionalColumns &&
        optionObject.additionalColumns.length > 0 &&
        dataPoints &&
        dataPoints.length > 0
      ) {
        optionObject.additionalColumns.forEach((column) => {
          if (column.yColNum) {
            // Find shift settings for this additional column
            const columnShiftSettings = optionObject.additionalColumnShifts?.find(
              shift => shift.columnId === column.id
            ) || { yShift: "none", yVal: "" };

            // Create options for this additional column
            const additionalOptions = {
              ...optionObject,
              yColNum: column.yColNum,
              presetName: column.presetName || "",
              yShift: columnShiftSettings.yShift,
              yShiftVal: columnShiftSettings.yVal,
            };

            // Get data points for this additional column
            const additionalDataPoints = handleCheckOneColumn(additionalOptions, skipClear);
            
            if (additionalDataPoints && additionalDataPoints.length > 0) {
              // Merge additional column data into main data points
              dataPoints = dataPoints.map((point, index) => {
                if (index < additionalDataPoints.length) {
                  const additionalPoint = additionalDataPoints[index];
                  const additionalValue = additionalPoint[column.presetName] || additionalPoint.y;
                  return {
                    ...point,
                    [column.presetName || "y"]: additionalValue,
                  };
                }
                return point;
              });
            }
          }
        });
      }
      
      // State clearing logic
      if (
        hasProperty(filesToProcess[currentFileIndex], "solved") &&
        filesToProcess[currentFileIndex].solved &&
        !skipClear
      ) {
        let filesToProcessCopy = deepCopyFiles(filesToProcess, recordedErrorLog);
        filesToProcessCopy[currentFileIndex].solved = false;
        setFilesToProcess(filesToProcessCopy);

        const updatedSolved = deepCopy(solvedList);
        updatedSolved[currentFileIndex] = null;
        setSolvedList(updatedSolved);
        setDone(false);
      }
      
      setProcessedContent(deepCopy(dataPoints));
    } catch (error) {
      recordedErrorLog("Check handler failure: ", error);
    }
  };

  const handleFinishLoad = () => {
    solveProcessing(solvedList);
  };

  const handleCancelLoad = () => {
    // Check if there are any solved files
    const solvedFiles = solvedList.filter((file) => file !== null);

    if (solvedFiles.length > 0) {
      // If there are solved files, save them before closing
      solveProcessing(solvedFiles);
    } else {
      // If no solved files, just cancel
      cancelProcessing();
    }
  };

  const handleButtonClick = (type) => {
    if (type === "file") {
      fileInputRef.current.click();
    } else if (type === "folder") {
      folderInputRef.current.click();
    }
  };

  async function handleFileChange(event) {
    try {
      const files = Array.from(event.target.files);
      const supportedExtensions = ["txt", "dat", "TXT", "DAT", "csv", "CSV"];

      const filteredFiles = files.filter((file) => {
        const fileExtension = file.name.split(".").pop();
        return supportedExtensions.includes(fileExtension);
      });

      const unsupportedFiles = files.filter(
        (file) => !filteredFiles.includes(file)
      );

      if (unsupportedFiles.length > 0) {
        const unsupportedFileNames = unsupportedFiles
          .map((file) => file.name)
          .join(", ");
        alert(
          `The following files have unsupported formats and will not be processed: ${unsupportedFileNames}`
        );
      }

      const queueLength = filesToProcess.length;
      // Append to existing lists instead of replacing them
      setFilesToProcess((prevFiles) => [...prevFiles, ...filteredFiles]);
      setSolvedList((prevSolved) => [
        ...prevSolved,
        ...Array(filteredFiles.length).fill(null),
      ]);

      // Load the first file's content immediately
      if (queueLength === 0 && filteredFiles.length > 0) {
        const firstFile = filteredFiles[0]; // Use 0 directly instead of currentFileIndex since we just set it
        readFile(
          firstFile,
          generateWarningObject,
          setWarnings,
          setNewWarningCount,
          limitedToast,
          recordedErrorLog
        )
          .then((readContent) => {
            setCurrentFileContent(readContent);
          })
          .catch(() => {
            setCurrentFileContent(null);
            setCurrentFileIndex(1); // Move to next file if this one fails
          });
      }
    } catch (error) {
      recordedErrorLog("File change handler failure: ", error);
    }
  }

  // Function to get tutorial data based on current tutorial and step
  const getTutorialData = async () => {
    switch (currentTutorial) {
      case "tutorial1":
        // Raman tutorial - use pre-processed Raman data
        return tutorialDataRaman;

      case "tutorial2":
        // Ellipsometry tutorial - load files based on step
        return await createTutorialFile(runningTutorial);

      case "tutorial3":
        // Dataloading tutorial - load files based on step
        return await createTutorial3Files();

      case "tutorial4":
        // Dataloading tutorial - load files based on step
        return await createTutorial4Files();

      default:
        return tutorialDataRaman;
    }
  };

  const handleTutorialButtonClick = async () => {
    try {
      // Get the appropriate tutorial data based on current tutorial and step
      const tutorialData = await getTutorialData();

// Normalize to an array of File objects regardless of tutorial
     let tutorialFiles;
     if (Array.isArray(tutorialData)) {
       // tutorial2 (sometimes) and tutorial3 (always) return an array of File objects
       tutorialFiles = tutorialData;
     } else if (tutorialData instanceof File) {
       tutorialFiles = [tutorialData];
     } else if (tutorialData && tutorialData.content && tutorialData.name) {
       // Raman-style object { content, name, type, lastModified }
       tutorialFiles = [
         new File([tutorialData.content], tutorialData.name, {
          type: tutorialData.type,
           lastModified: tutorialData.lastModified,
         }),
       ];
     } else {
       throw new Error("Unexpected tutorial data format");
     }

      // Add all files to filesToProcess
      setFilesToProcess((prevFiles) => [...prevFiles, ...tutorialFiles]);
      setSolvedList((prevSolved) => [
        ...prevSolved,
        ...new Array(tutorialFiles.length).fill(null),
      ]);

      // NEW: Monitor state change for tutorial progression
      tutorialFiles.forEach((file) => {
        monitorStateChange("tutorial_file_added_to_queue", {
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          tutorial: currentTutorial,
        });
      });

      // If it's the first file, load its content
      if (filesToProcess.length === 0 && tutorialFiles.length > 0) {
        readFile(
          tutorialFiles[0],
          generateWarningObject,
          setWarnings,
          setNewWarningCount,
          limitedToast,
          recordedErrorLog
        )
          .then((readContent) => {
            setCurrentFileContent(readContent);
            // Advance to tutorial step 4 after the data is loaded
            // Update tutorial step to indicate data has been loaded
          })
          .catch((error) => {
            console.error("Failed to read tutorial file:", error);
            setCurrentFileContent(null);
            setCurrentFileIndex(1);
            limitedToast("Failed to read tutorial file");
          });
      }
    } catch (error) {
      console.error("Tutorial file loading failure:", error);
      recordedErrorLog("Tutorial file loading failure: ", error);
      limitedToast("Failed to load tutorial file");
    }
  };

  return (
    <div className="fileProcessWindow" id="fileProcessWindow">
      <div className="deleteGraphButtonContainer">
        <CloseButton onClose={handleCancelLoad} />
        {/* <HighlightOffIcon 
          className="deleteGraphIcon"
          onClick={handleCancelLoad}
        /> */}
      </div>
      <div className="leftSide">
        <div className="leftTitle">File queue:</div>
        <div className="leftContent">
          <FileList
            selectedFileIndex={currentFileIndex}
            filesToProcess={filesToProcess}
            setSelectedFile={setCurrentFileIndex}
          />
          <div className="buttonArea">
            <CustomButton
              text="Files"
              extraClassnames={["noMargin", "dataManagerButton"]}
              handleClick={() => handleButtonClick("file")}
              id="file-upload-button"
            />
            <CustomButton
              text="Folder"
              extraClassnames={["noMargin", "dataManagerButton"]}
              handleClick={() => handleButtonClick("folder")}
              id="folder-upload-button"
            />
            {tutorialOpen && (
              <CustomButton
                text="Tutorial Data"
                extraClassnames={["noMargin", "dataManagerButton"]}
                handleClick={handleTutorialButtonClick}
                id="tutorial-next-button"
              />
            )}
            {/* <Button
              variant="contained"
              className="finishButton"
              onClick={() => handleButtonClick("file")}
              id="file-upload-button"
            >
              Files
            </Button>
            <Button
              variant="contained"
              className="finishButton"
              onClick={() => handleButtonClick("folder")}
              id="folder-upload-button"
            >
              Folder
            </Button> */}
          </div>
          <input
            ref={fileInputRef}
            id="file-upload-input"
            type="file"
            multiple
            accept=".txt,.dat,.csv"
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
          <input
            ref={folderInputRef}
            type="file"
            multiple
            webkitdirectory="true"
            accept=".txt,.dat,.csv"
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
        </div>
      </div>
      <div className="rightSide">
        <RawAndSolved
          rawFileContent={currentFileContent}
          processedContent={processedContent}
          options={currentOptions}
        />
        <div className="divider" />
        <ProcessingOptions
          save={handleSave}
          apply={handleApply}
          applyAll={handleApplyToAll}
          check={handleCheck}
          modalId={modalId}
          fileContent={currentFileContent}
          processedFileDetails={solvedList[currentFileIndex]}
          processedContent={processedContent}
          cancel={handleCancelLoad}
          onOptionsChange={setCurrentOptions}
          fileIndex={currentFileIndex}
          fileName={filesToProcess[currentFileIndex]?.name}
        />
      </div>
    </div>
  );
}

export default FileProcessingWindow;
